/**
 * Subdomain Utilities
 * Helper functions for detecting and handling wildcard subdomain routing
 */

import { customDomains } from '../config/customDomains';

const MAIN_DOMAIN = 'chessweb.site';
const EXCLUDED_SUBDOMAINS = ['www', 'mail', 'ftp', 'admin', 'api'];

/**
 * Check if current hostname is a subdomain (excluding www)
 * @returns {boolean}
 */
export const isSubdomain = () => {
  const hostname = window.location.hostname;

  // Not a subdomain if:
  // - It's localhost
  // - It's the main domain
  // - It's an excluded subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }

  if (hostname === MAIN_DOMAIN || hostname === `www.${MAIN_DOMAIN}`) {
    return false;
  }

  // Check if it ends with our domain
  if (!hostname.endsWith(`.${MAIN_DOMAIN}`)) {
    return false;
  }

  // Extract subdomain part
  const subdomain = hostname.replace(`.${MAIN_DOMAIN}`, '');

  // Check if it's an excluded subdomain
  if (EXCLUDED_SUBDOMAINS.includes(subdomain)) {
    return false;
  }

  // Make sure there's only one subdomain level (no nested subdomains)
  if (subdomain.includes('.')) {
    return false;
  }

  return true;
};

/**
 * Extract username from subdomain
 * @returns {string|null} - Username from subdomain, or null if not a subdomain
 */
export const getSubdomainUsername = () => {
  if (!isSubdomain()) {
    return null;
  }

  const hostname = window.location.hostname;
  const subdomain = hostname.replace(`.${MAIN_DOMAIN}`, '');

  return subdomain;
};

/**
 * Get the canonical URL for the current page
 * For subdomain access, returns the main domain URL
 * @returns {string}
 */
export const getCanonicalUrl = () => {
  const username = getSubdomainUsername();

  if (username) {
    // If accessed via subdomain, canonical URL is the main domain /me/ route
    return `https://${MAIN_DOMAIN}/me/${username}`;
  }

  // Otherwise, return current URL
  return window.location.href;
};

/**
 * Get the subdomain URL for a given username
 * @param {string} username
 * @returns {string}
 */
export const getSubdomainUrl = (username) => {
  if (!username) {
    return null;
  }

  return `https://${username}.${MAIN_DOMAIN}`;
};

/**
 * Check if the current environment supports subdomains
 * (not localhost, not development)
 * @returns {boolean}
 */
export const supportsSubdomains = () => {
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.includes('127.0.0');
};

/**
 * Get all domains for a username: { customDomain, subdomain, basePath, primaryUrl }
 * primaryUrl prefers custom domain > subdomain > base path
 * Subdomain is only available for premium users — free users get basePath
 * @param {string} username
 * @param {{ isPremium?: boolean }} options
 * @returns {{ customDomain: string|null, subdomain: string, basePath: string, primaryUrl: string, primaryLabel: string }}
 */
export const getUserDomains = (username, { isPremium = true } = {}) => {
  if (!username) return { customDomain: null, subdomain: '', basePath: '', primaryUrl: '', primaryLabel: '' };

  const lower = username.toLowerCase();
  const customDomain = Object.entries(customDomains)
    .find(([, u]) => u.toLowerCase() === lower)?.[0] || null;
  const subdomain = `${username}.${MAIN_DOMAIN}`;
  const basePath = `${MAIN_DOMAIN}/me/${username}`;

  let primaryUrl, primaryLabel;
  if (customDomain) {
    primaryUrl = `https://${customDomain}`;
    primaryLabel = customDomain;
  } else if (isPremium) {
    primaryUrl = `https://${subdomain}`;
    primaryLabel = subdomain;
  } else {
    primaryUrl = `https://${basePath}`;
    primaryLabel = basePath;
  }

  return { customDomain, subdomain, basePath, primaryUrl, primaryLabel };
};
